import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Clock, Send, Trash2, Users } from "lucide-react";
import { frozenDate } from "@/lib/notificationDisplay";

interface WaitlistEntry {
  id: string;
  user_id: string;
  court_id: string;
  date: string;
  start_time: string | null;
  end_time: string | null;
  status: string;
  position: number;
  offered_at: string | null;
  expires_at: string | null;
  [key: string]: unknown;
}

function CountdownTimer({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation("admin");
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft(t("waitlist.expiredLabel"));
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${String(secs).padStart(2, "0")}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return <span className="font-mono text-xs text-primary font-semibold">{timeLeft}</span>;
}

export default function AdminWaitlist() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const qc = useQueryClient();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["admin-waitlist", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("waitlist")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("date")
        .order("position");
      if (error) throw error;
      return (data ?? []) as WaitlistEntry[];
    },
    enabled: !!organization?.id,
    refetchInterval: 10000,
  });

  // Fetch profiles for user names
  const userIds = [...new Set(entries.map((e) => e.user_id))];
  const { data: profiles = [] } = useQuery({
    queryKey: ["waitlist-profiles", userIds.join(",")],
    queryFn: async () => {
      if (userIds.length === 0) return [];
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name").in("id", userIds);
      return data ?? [];
    },
    enabled: userIds.length > 0,
  });

  const { data: courts = [] } = useQuery({
    queryKey: ["waitlist-courts"],
    queryFn: async () => {
      const { data } = await supabase.from("courts").select("id, name");
      return data ?? [];
    },
  });

  const profileMap = new Map(profiles.map((p) => [p.id, p.full_name]));
  const courtMap = new Map(courts.map((c) => [c.id, c.name]));

  const offerMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("waitlist")
        .update({
          status: "offered",
          offered_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
        } as Record<string, unknown>)
        .eq("id", id);
      if (error) throw error;

      // Send notification
      const entry = entries.find((e) => e.id === id);
      if (entry) {
        await supabase.from("notifications").insert({
          user_id: entry.user_id,
          organization_id: organization!.id,  // PR-A: org-scope per Phase 1 RLS
          type: "waitlist_offer",
          // FEL-012c: utan action_url navigerar Notifications.tsx ingenstans.
          action_url: "/book",
          title: t("waitlist.slotAvailable"),
          body: t("waitlist.offerNotifBody", { court: courtMap.get(entry.court_id) ?? t("waitlist.courtFallback"), date: frozenDate(entry.date), time: entry.start_time?.slice(0, 5) }),
          data: { waitlist_id: entry.id, court_id: entry.court_id, date: entry.date, start_time: entry.start_time, end_time: entry.end_time },
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-waitlist"] });
      toast.success(t("admin:toast.offerSent"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("waitlist").update({ status: "expired" } as Record<string, unknown>).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-waitlist"] });
      toast.success(t("admin:toast.removedFromWaitlist"));
    },
  });

  // "Offer to all" — expire all waiters and open the slot
  const offerToAllMutation = useMutation({
    mutationFn: async ({ courtId, date, startTime }: { courtId: string; date: string; startTime: string }) => {
      const { error } = await supabase
        .from("waitlist")
        .update({ status: "expired" } as Record<string, unknown>)
        .eq("court_id", courtId)
        .eq("date", date)
        .eq("start_time", startTime)
        .in("status", ["waiting", "offered"]);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-waitlist"] });
      toast.success(t("admin:toast.queueCleared"));
    },
  });

  if (isLoading) return <Skeleton className="h-40" />;

  const waiting = entries.filter((e) => e.status === "waiting");
  const offered = entries.filter((e) => e.status === "offered");
  const expired = entries.filter((e) => e.status === "expired" || e.status === "accepted");

  // Group waiting entries by slot
  const slotGroups = new Map<string, WaitlistEntry[]>();
  waiting.forEach((e) => {
    const key = `${e.court_id}|${e.date}|${e.start_time}`;
    if (!slotGroups.has(key)) slotGroups.set(key, []);
    slotGroups.get(key)!.push(e);
  });

  const renderEntry = (e: WaitlistEntry, showActions = true) => (
    <Card key={e.id}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{profileMap.get(e.user_id) || t("common.unknown")}</p>
          <p className="text-xs text-muted-foreground">
            {courtMap.get(e.court_id) || t("waitlist.courtFallback")} · {format(new Date(e.date + "T00:00:00"), "d MMM", { locale: sv })} {e.start_time?.slice(0, 5)}–{e.end_time?.slice(0, 5)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {e.status === "offered" && e.expires_at && (
            <CountdownTimer expiresAt={e.expires_at} />
          )}
          <Badge variant={e.status === "offered" ? "default" : e.status === "waiting" ? "secondary" : "outline"}>
            {e.status === "offered" ? t("waitlist.offered") : e.status === "waiting" ? `${t("waitlist.position")} ${e.position}` : e.status === "accepted" ? t("waitlist.accepted") : t("waitlist.expired")}
          </Badge>
        </div>
        {showActions && (
          <div className="flex gap-1">
            {e.status === "waiting" && (
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => offerMutation.mutate(e.id)} title={t("waitlist.offerTime")}>
                <Send className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteMutation.mutate(e.id)} title={t("waitlist.remove")}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <h3 className="font-semibold flex items-center gap-2">
        <Clock className="h-4 w-4" /> {t("waitlist.title")} ({waiting.length + offered.length})
      </h3>

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active">{t("waitlist.active")} ({waiting.length + offered.length})</TabsTrigger>
          <TabsTrigger value="history">{t("waitlist.historyTab")} ({expired.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="active" className="space-y-2 mt-3">
          {waiting.length + offered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{t("waitlist.noActiveEntries")}</p>
          ) : (
            <>
              {offered.map((e) => renderEntry(e))}
              {waiting.map((e) => renderEntry(e))}

              {/* Offer to all buttons per slot group */}
              {slotGroups.size > 0 && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">{t("waitlist.quickActions")}</p>
                  {Array.from(slotGroups.entries()).map(([key, items]) => {
                    const first = items[0];
                    return (
                      <Button
                        key={key}
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-xs"
                        onClick={() => offerToAllMutation.mutate({
                          courtId: first.court_id,
                          date: first.date,
                          startTime: first.start_time,
                        })}
                      >
                        <Users className="h-3.5 w-3.5" />
                        {t("waitlist.openForAll")}: {courtMap.get(first.court_id) || t("waitlist.courtFallback")} {first.start_time?.slice(0, 5)} ({items.length} {t("waitlist.inQueue")})
                      </Button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </TabsContent>
        <TabsContent value="history" className="space-y-2 mt-3">
          {expired.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{t("waitlist.noHistory")}</p>
          ) : (
            expired.map((e) => renderEntry(e, false))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
